import PropTypes from 'prop-types';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { makeStyles } from '@mui/styles';
import useTheme from '@mui/material/styles/useTheme';
import AppBar from '@mui/material/AppBar';
import CssBaseline from '@mui/material/CssBaseline';
import Toolbar from '@mui/material/Toolbar';
import useMediaQuery from '@mui/material/useMediaQuery';

// third-party
import clsx from 'clsx';

// project imports
import Breadcrumbs from './../../ui-component/extended/Breadcrumbs';
import Header from './Header';
import Sidebar from './Sidebar';
import CopilotoPanel from './CopilotoPanel';
import PlanExpirationBanner from './PlanExpirationBanner';
import WhatsappWApiConnectionBanner from './WhatsappWApiConnectionBanner';
import Customization from './../Customization';
import navigation from './../../menu-items';
import { appDrawerWidth, drawerWidth } from '../../store/constant';
import { SET_MENU, SET_COPILOTO } from './../../store/actions';

// assets
import { IconChevronRight } from '@tabler/icons';
import HideOnScroll from '../../themes/hideOnScroll';

// style constant
const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex'
    },
    // appBar: {
    //     backgroundColor: theme.palette.background.default
    // },
    appBar: {
        backgroundColor: theme.palette.background.default, //backgroundColor: '#22233A',
        height: 48,
        transition: theme.transitions.create(['top'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
        })
    },
    toolbar: {
        minHeight: 48
    },
    appBarWidth: {
        transition: theme.transitions.create('width'),
        backgroundColor: theme.palette.background.default
    },
    content: {
        ...theme.typography.mainContent,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
        }),
        [theme.breakpoints.up('md')]: {
            marginLeft: -(drawerWidth - 20),
            width: `calc(100% - ${drawerWidth}px)`
        },
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '16px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '16px',
            marginRight: '10px'
        }
    },
    contentShift: {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen
        }),
        marginLeft: 0,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px'
        }
    },
    // Painel do Assistente IA (lateral direito) - NÃO é um Drawer por cima
    // de tudo (isso é o que o Sidebar esquerdo faz, e foi explicitamente
    // pedido pra não repetir aqui) - é uma segunda caixa igual à
    // `content` (mesmo fundo/cantos arredondados/altura), ao lado dela,
    // dentro do mesmo espaço onde a tela é renderizada. `content` perde
    // `appDrawerWidth` de largura quando o painel abre, pra sobrar espaço
    // sem precisar de scroll horizontal.
    contentCopilotoShift: {
        [theme.breakpoints.up('md')]: {
            width: `calc(100% - ${drawerWidth}px - ${appDrawerWidth}px - 20px)`
        }
    },
    copilotoBox: {
        ...theme.typography.mainContent,
        padding: 0,
        width: appDrawerWidth,
        minWidth: appDrawerWidth,
        minHeight: 0,
        height: 'calc(100vh - 108px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flexShrink: 0
    }
}));

//-----------------------|| MAIN LAYOUT ||-----------------------//

const MainLayout = ({ children }) => {
    const classes = useStyles();
    const theme = useTheme();
    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));

    // Handle left drawer
    const leftDrawerOpened = useSelector((state) => state.customization.opened);
    const dispatch = useDispatch();
    const handleLeftDrawerToggle = () => {
        dispatch({ type: SET_MENU, opened: !leftDrawerOpened });
    };

    // Handle painel do Assistente IA (direito)
    const copilotoOpened = useSelector((state) => state.customization.copilotoOpened);
    const handleCopilotoToggle = () => {
        dispatch({ type: SET_COPILOTO, opened: !copilotoOpened });
    };

    React.useEffect(() => {
        dispatch({ type: SET_MENU, opened: !matchDownMd });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matchDownMd]);

    return (
        <div className={classes.root}>
            <CssBaseline />
            {/* header */}

            <AppBar
                enableColorOnDark
                position="fixed"
                color="inherit"
                elevation={3}
                className={leftDrawerOpened ? classes.appBarWidth : classes.appBar}
            >
                <Toolbar className={classes.toolbar}>
                    <Header handleLeftDrawerToggle={handleLeftDrawerToggle} handleCopilotoToggle={handleCopilotoToggle} />
                </Toolbar>
            </AppBar>

            {/* drawer */}
            <Sidebar drawerOpen={leftDrawerOpened} drawerToggle={handleLeftDrawerToggle} />

            {/* main content */}
            <main
                className={clsx([
                    classes.content,
                    {
                        [classes.contentShift]: leftDrawerOpened,
                        [classes.contentCopilotoShift]: copilotoOpened
                    }
                ])}
            >
                {/* <Main open={leftDrawerOpened}> */}
                <PlanExpirationBanner />
                <WhatsappWApiConnectionBanner />
                {/* breadcrumb */}
                <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
                <div>{children}</div>
                {/* </Main> */}
            </main>

            {/* painel do Assistente IA - mesma "caixa" visual do conteúdo
            principal, ao lado dela, não um drawer por cima da tela toda */}
            <CopilotoPanel open={copilotoOpened} onClose={handleCopilotoToggle} boxClassName={classes.copilotoBox} />

            <Customization />
        </div>
    );
};

MainLayout.propTypes = {
    children: PropTypes.node
};

export default MainLayout;
